import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api-client";
import type {
  BatchListResponse,
  BatchResponse,
  SetBatchCharacteristicsRequest,
} from "@/types/batches.types";

// Spec 084 — a batelada: características efetivas, origem e correção.
export const batchKey = (runId: string) => ["batch", runId] as const;
export const lineBatchesKey = (lineId: string) => ["batches", lineId] as const;

export function useBatch(lineId: string, runId: string | null) {
  return useQuery<BatchResponse>({
    queryKey: batchKey(runId ?? "none"),
    enabled: !!runId,
    queryFn: () => api.get<BatchResponse>(`/api/v1/lines/${lineId}/production-runs/${runId}/batch`),
  });
}

export function useLineBatches(lineId: string, limit = 10) {
  return useQuery<BatchListResponse>({
    queryKey: [...lineBatchesKey(lineId), limit],
    queryFn: () => api.get<BatchListResponse>(`/api/v1/lines/${lineId}/batches?limit=${limit}`),
  });
}

/**
 * Corrigir um valor muda o que as HERDEIRAS mostram, e o cliente não sabe quem são. Por isso a
 * invalidação é por prefixo em batelada, lista de bateladas e relatório (que recorta por valor).
 */
export function useSetBatchCharacteristics(lineId: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: SetBatchCharacteristicsRequest) =>
      api.put<BatchResponse>(
        `/api/v1/lines/${lineId}/production-runs/${runId}/characteristics`,
        body
      ),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["batch"] });
      void qc.invalidateQueries({ queryKey: ["batches"] });
      void qc.invalidateQueries({ queryKey: ["production-report"] });
    },
  });
}

/**
 * Associar origem muda a HERANÇA, e a herança é derivada: o que esta batelada passa a mostrar, e o
 * que as bateladas abaixo dela mostram, muda junto. O cliente não sabe quem são as herdeiras — por
 * isso a invalidação é por prefixo, igual à correção de valores.
 */
export function useSetRunOrigins(lineId: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (originRunIds: string[]) =>
      api.put<BatchResponse>(`/api/v1/lines/${lineId}/production-runs/${runId}/origins`, {
        originRunIds,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ["batch"] });
      void qc.invalidateQueries({ queryKey: ["batches"] });
      void qc.invalidateQueries({ queryKey: ["production-report"] });
    },
  });
}

export function useSetBatchCode(lineId: string, runId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (batchCode: string) =>
      api.patch<BatchResponse>(`/api/v1/lines/${lineId}/production-runs/${runId}/batch-code`, {
        batchCode,
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: batchKey(runId) });
      void qc.invalidateQueries({ queryKey: lineBatchesKey(lineId) });
      void qc.invalidateQueries({ queryKey: ["production", "active", lineId] });
    },
  });
}
